import { environment } from './../../../environments/environment';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, filter, mergeMap, share } from 'rxjs/operators';
import * as _ from 'lodash';
import { Store } from '@ngrx/store';
import { getAuth } from './../../../app/@store/auth/auth.selectors';
import { HttpParameterCodec } from '@angular/common/http';
import { ConfigService } from '@gridscale/ingrid/helper/services/config.service';

export interface PublicApiErrorResponse extends HttpErrorResponse {
  method: string;
}

export class CustomHttpParamEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

// unwrap Promise Return Type... (@see https://jpwilliams.dev/how-to-unpack-the-return-type-of-a-promise-in-typescript)
type Unwrap<T> = T extends Promise<infer U> ? U : T extends (...args: any) => Promise<infer U> ? U : T extends (...args: any) => infer U ? U : T;

@Injectable({
  providedIn: 'root'
})
export class PublicPartnerApiService {
  readonly #configService = inject(ConfigService);

  localMockApi: string | undefined = undefined;//environment.production ? 'https://45.12.50.123.nip.io' : 'http://localhost:8080';

  logger: ((e: PublicApiErrorResponse) => void)[] = [];


  currentAuth: ReturnType<typeof getAuth>;

  current_endpoint = this.#configService.partnerApi;

  constructor(private http: HttpClient, private store: Store) {

    this.#configService.ready$.pipe(
      filter(ready => !!ready),
    ).subscribe(() => {
      this.current_endpoint = this.#configService.partnerApi;
    })

    this.store
      .select(getAuth)
      .pipe(filter(_auth => _auth !== undefined))
      .subscribe(_auth => {
        this.currentAuth = _auth;
      });
  }

  /**
   * Wrapper function: Calls the method that returns a promise and returns an Observable from its Promise
   * use this for example for the link functions
   *
   * @param _promiseReturningFunc the api method that would return the promise (e.g. 'Server.list', 'Storage.patch' ...)
   * @param _callContext The context where this function should be called
   * @param _functionArgs arguments for that function
   */
  fromFunction$<T>(_promiseReturningFunc: () => Promise<Unwrap<T>>, _callContext: any, ..._functionArgs: any): Observable<Unwrap<T>> {
    return of(
      mergeMap(() => {
        return from(_promiseReturningFunc.apply(_callContext, _functionArgs)) as Observable<Unwrap<T>>;
      })
    ).pipe(share()) as Observable<Unwrap<T>>;
  }

  patch<T>(url: string, params: any | { [param: string]: string | string[] } = {}, errorWhitelist?: string[] | number[]): Observable<T> {
    return this._patch(url, params, errorWhitelist);
  }

  get<T>(url: string, params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }, errorWhitelist?: string[] | number[]): Observable<T> {

    let current_endpoint = this.current_endpoint;
    if (this.localMockApi && url === '/api/v1/skin') {
      current_endpoint = this.localMockApi;
    }
    return this.http
      .get<T>(this.getUrl(current_endpoint, this.resolveUrlPlaceholder(url)), {
        headers: this._headers(),
        withCredentials: false,
        params
      })
      .pipe(
        catchError((err: HttpErrorResponse, caught: Observable<T>) => {
          if (!this.checkWhitelisted(err, errorWhitelist || [])) {
            this.logger.forEach(_logger => {
              _logger({ ...err, method: 'GET' } as PublicApiErrorResponse);
            });
            return throwError(() => err);
          }
          return caught;
        })
      );
  }

  _patch<T>(url: string, body: any, errorWhitelist?: string[] | number[]): Observable<T> {

    let current_endpoint = this.current_endpoint;

    if (this.localMockApi && url === '/api/v1/skin') {
      current_endpoint = this.localMockApi;
    }

    return this.http
      .patch<T>(this.getUrl(current_endpoint, this.resolveUrlPlaceholder(url)), this.resolveBodyPlaceholder(body), {
        headers: this._headers(),
        withCredentials: false
      })
      .pipe(
        catchError((err: HttpErrorResponse, caught: Observable<T>) => {
          if (!this.checkWhitelisted(err, errorWhitelist || [])) {
            this.logger.forEach(_logger => {
              _logger({ ...err, method: 'PATCH' } as PublicApiErrorResponse);
            });
            return throwError(() => err);
          }
          return caught;
        })
      );
  }

  post<T>(url: string, body: any, errorWhitelist?: string[] | number[]): Observable<T> {
    return this.http
      .post<T>(this.getUrl(this.current_endpoint, this.resolveUrlPlaceholder(url)), this.resolveBodyPlaceholder(body), {
        headers: this._headers(),
        withCredentials: false,
      })
      .pipe(
        catchError((err: HttpErrorResponse, caught: Observable<T>) => {
          if (!this.checkWhitelisted(err, errorWhitelist || [])) {
            this.logger.forEach(_logger => {
              _logger({ ...err, method: 'POST' } as PublicApiErrorResponse);
            });
            //return throwError(catched);
          }
          throw err;
        })
      );
  }

  postForm<T>(url: string, body: FormData, errorWhitelist?: string[] | number[]): Observable<T> {

    let current_endpoint = this.current_endpoint
    if (this.localMockApi && url.indexOf('/api/v1/skin') === 0) {
      current_endpoint = this.localMockApi;
    }

    return this.http
      .post<T>(this.getUrl(current_endpoint, this.resolveUrlPlaceholder(url)), body)
      .pipe(
        catchError((err: HttpErrorResponse, caught: Observable<T>) => {
          if (!this.checkWhitelisted(err, errorWhitelist || [])) {
            this.logger.forEach(_logger => {
              _logger({ ...err, method: 'POST' } as PublicApiErrorResponse);
            });
            return throwError(() => err);
          }
          return caught;
        })
      );
  }


  delete<T>(url: string, errorWhitelist?: string[] | number[]): Observable<T> {

    let current_endpoint = this.current_endpoint
    if (this.localMockApi && url.indexOf('/api/v1/skin') === 0) {
      current_endpoint = this.localMockApi;
    }

    return this.http
      .delete<T>(this.getUrl(current_endpoint, this.resolveUrlPlaceholder(url)), {
        headers: this._headers(),
        withCredentials: false
      })
      .pipe(
        catchError((err: HttpErrorResponse, caught: Observable<T>) => {
          if (!this.checkWhitelisted(err, errorWhitelist || [])) {
            this.logger.forEach(_logger => {
              _logger({ ...err, method: 'DELETE' } as PublicApiErrorResponse);
            });
            return throwError(() => err);
          }
          return caught;
        })
      );
  }

  addLogger(callback: (e: PublicApiErrorResponse) => void) {
    this.logger.push(callback);
  }

  private checkWhitelisted(theError: HttpErrorResponse, whitelist: number[] | string[]): boolean {
    let whitelisted = false;
    if (whitelist) {
      whitelist.forEach(_whitelist => {
        if (typeof _whitelist === 'number' && _whitelist === theError.status) {
          whitelisted = true;
        } else if (typeof _whitelist === 'string' && theError.message.indexOf(_whitelist) >= 0) {
          whitelisted = true;
        }
      });
    }
    return whitelisted;
  }

  /**
   * We try to replace all UUIDs with the current values
   *
   * @param body Body of the payload where we want to replace the contetn
   * @returns
   */
  private resolveBodyPlaceholder<T>(body: T): T {
    if (!_.isObject(body)) {
      return body;
    }

    let tmpBody: any = { ...body };
    // if the current Object is an array, we stay with an array
    if (_.isArray(body)) {
      tmpBody = [...body];
    }

    _.forEach(tmpBody, (_val: any, _key) => {
      if (typeof _val === 'string') {
        tmpBody[_key] = this.resolveUrlPlaceholder(_val);
      } else if (_.isObject(_val)) {
        tmpBody[_key] = this.resolveBodyPlaceholder(_val);
      }
    });
    return tmpBody;
  }

  private resolveUrlPlaceholder(url: string): string {
    return url.replace(/##([A-Z_]+)##/g, (_all, _match) => {
      if (this.currentAuth === undefined) {
        return _all;
      }
      switch (_match) {
        case 'CURRENT_USER_UUID':
          return this.currentAuth.user_uuid;

        case 'CURRENT_PARTNER_UUID':
          return this.currentAuth.partner_uuid;

        default:
          return _all;
      }
    });
  }

  private _headers(additionalHeaders: Record<string, string> = {}): HttpHeaders {
    const headersConfig = {
      'x-api-client': 'partnerpanel2',
      'X-Auth-UserId': this.currentAuth?.user_uuid || '',
      'X-Auth-Token': this.currentAuth?.token || '',
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...additionalHeaders
    };

    return new HttpHeaders(headersConfig);
  }

  /**
 * returns the URL or a new URL from endpoint overrides
 */
  private getUrl(url: string, path: string) {
    let newUrl = url;

    if (environment.endpointOverrides && typeof (environment.endpointOverrides) === 'object') {
      _.forEach(environment.endpointOverrides, (_overrideEndpoint, _overridePath) => {

        if (_overridePath.match(/^\/(.*)\/$/) && path.split('?')[0].match(new RegExp(RegExp.$1))) {
          newUrl = _overrideEndpoint;

        } else if (path.split('?')[0] === _overridePath) {
          newUrl = _overrideEndpoint;

        } else {
          return true;
        }

        return false;
      });
    }

    return newUrl + path;
  }
}
